/**
 * chatPdf.js -- exports an AI Companion conversation to a PDF.
 *
 * Drawn as text with jsPDF, like the Annual Client Summary download
 * (AiClientSummary/summaryPdf.js): the output stays selectable and small, and a
 * long chat breaks across pages instead of being clipped. Replies are Markdown,
 * so this renders the subset the model writes -- headings, paragraphs, bullet
 * and numbered lists, tables, block quotes, code and rules, with bold, italic
 * and code inline.
 *
 * Every page carries the export timestamp in its footer: replies hold patient
 * information, and a saved copy outlives the conversation it came from.
 */

import jsPDF from 'jspdf';
import moment from 'moment';
import fieldworkerLogo from './fieldworkerLogo.png';

const TITLE = 'AI Companion Conversation';
const DISCLAIMER = 'AI Companion assists with patient care and case documentation. Always verify clinical information.';
const TIMESTAMP_FORMAT = 'MM/DD/YYYY, hh:mm A';
const TIME_FORMAT = 'hh:mm A';

// The FieldWorker wordmark (public/landing_logo.png cropped to the logo), drawn
// beside "Generated using" in each page's footer. Its pixel size sets the aspect ratio.
const LOGO = { width: 391, height: 77, drawWidth: 50 };
const BRAND = [63, 167, 214];

const PAGE = { format: 'letter', unit: 'pt' };
const MARGIN = 48;
const FOOTER_HEIGHT = 28;
const BODY_SIZE = 10;
const LIST_INDENT = 14;
const HEADING_SIZES = [14, 12.5, 11.5, 10.5, 10, 10];

// Bubbles follow the chat screen (AiCompanion.css): prompts on the right in blue,
// replies on the left in a bordered white bubble, errors on the left in red. A
// reply is wider than the screen's 80% so its tables and lists keep their room.
const BUBBLE_PAD_X = 12;
const BUBBLE_PAD_Y = 8;
const BUBBLE_RADIUS = 8;
const PROMPT_MAX_WIDTH = 0.8;
const REPLY_WIDTH = 0.92;

const COLORS = {
  text: [33, 33, 33],
  muted: [110, 110, 110],
  primary: [25, 118, 210],
  warning: [237, 108, 2],
  code: [80, 80, 80],
  rule: [210, 210, 210],
  shade: [244, 246, 248],
  promptBubble: [63, 167, 214],
  promptText: [255, 255, 255],
  replyBorder: [226, 232, 240],
  errorBubble: [253, 236, 234],
  errorText: [198, 40, 40],
};

const ROLE_LABELS = { user: 'You', assistant: 'AI Companion', error: 'Error' };
const ROLE_COLORS = { user: COLORS.muted, assistant: COLORS.primary, error: COLORS.errorText };

/* jsPDF's built-in fonts only cover Latin-1. Model output routinely carries
   curly quotes, dashes and ellipses, which would otherwise print as garbage. */
const CHAR_MAP = {
  '‘': "'", '’': "'", '‚': "'", '‛': "'",
  '“': '"', '”': '"', '„': '"',
  '–': '-', '—': '-', '−': '-', '‐': '-', '‑': '-',
  '•': '-', '·': '-',
  '…': '...', ' ': ' ', ' ': ' ', ' ': ' ',
};

const clean = (value) => String(value === null || value === undefined ? '' : value)
  .normalize('NFKC')
  .replace(/[‘-„‐-—−•·…   ]/g, (c) => CHAR_MAP[c] || '')
  .replace(/[^\n\x20-\xFF]/g, '');

/* ------------------------------------------------------------- Markdown */

const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/;
const RULE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const FENCE = /^\s{0,3}(```|~~~)/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

const isTableStart = (lines, i) => lines[i].includes('|')
  && i + 1 < lines.length
  && lines[i + 1].includes('|')
  && TABLE_DIVIDER.test(lines[i + 1]);

const startsBlock = (lines, i) => {
  const line = lines[i];
  return FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line)
    || LIST_ITEM.test(line) || isTableStart(lines, i);
};

const splitRow = (line) => {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|')) row = row.slice(0, -1);
  return row.split('|').map((cell) => cell.trim());
};

/** Splits a reply into blocks: heading, paragraph, list, table, quote, code, rule. */
const parseMarkdown = (markdown) => {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').replace(/<br\s*\/?>/gi, ' ').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }

    const fence = line.match(FENCE);
    if (fence) {
      const body = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith(fence[1])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1;
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    if (RULE.test(line)) {
      blocks.push({ type: 'rule' });
      i += 1;
      continue;
    }

    if (isTableStart(lines, i)) {
      const header = splitRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    if (QUOTE.test(line)) {
      const body = [];
      while (i < lines.length && QUOTE.test(lines[i])) {
        body.push(lines[i].match(QUOTE)[1].trim());
        i += 1;
      }
      blocks.push({ type: 'quote', text: body.filter(Boolean).join(' ') });
      continue;
    }

    if (LIST_ITEM.test(line)) {
      // Nesting follows indentation: each deeper indent than the one above opens
      // a level, a shallower one closes levels back to the matching indent.
      const items = [];
      const indents = [];
      while (i < lines.length) {
        const item = lines[i].match(LIST_ITEM);
        if (item) {
          const indent = item[1].replace(/\t/g, '    ').length;
          while (indents.length && indent < indents[indents.length - 1]) indents.pop();
          if (!indents.length || indent > indents[indents.length - 1]) indents.push(indent);
          items.push({
            depth: indents.length - 1,
            marker: /\d/.test(item[2]) ? `${parseInt(item[2], 10)}.` : null,
            text: item[3],
          });
          i += 1;
        } else if (lines[i].trim() && !startsBlock(lines, i)) {
          items[items.length - 1].text += ` ${lines[i].trim()}`;
          i += 1;
        } else if (!lines[i].trim() && i + 1 < lines.length && LIST_ITEM.test(lines[i + 1])) {
          i += 1;
        } else {
          break;
        }
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    const body = [line.trim()];
    i += 1;
    while (i < lines.length && lines[i].trim() && !startsBlock(lines, i)) {
      body.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: 'paragraph', text: body.join(' ') });
  }

  return blocks;
};

const INLINE = /`([^`]+)`|\*\*(.+?)\*\*|__(.+?)__|\*(\S(?:.*?\S)?)\*|_(\S(?:.*?\S)?)_|\[([^\]]+)\]\(([^)\s]+)\)/g;

/** Splits a line of Markdown into styled runs: { text, bold, italic, code }. */
const parseInline = (text, style = {}) => {
  const runs = [];
  const push = (value, runStyle) => {
    if (value) runs.push({ ...runStyle, text: value });
  };
  let last = 0;
  for (const match of String(text || '').matchAll(INLINE)) {
    const [whole, code, bold, boldAlt, italic, italicAlt, linkText, linkUrl] = match;
    const start = match.index;
    // An underscore inside a word (snake_case, a file name) is not emphasis.
    if (italicAlt !== undefined
      && (/\w/.test(text[start - 1] || '') || /\w/.test(text[start + whole.length] || ''))) continue;

    push(text.slice(last, start), style);
    if (code !== undefined) push(code, { ...style, code: true });
    else if (bold !== undefined || boldAlt !== undefined) runs.push(...parseInline(bold ?? boldAlt, { ...style, bold: true }));
    else if (italic !== undefined || italicAlt !== undefined) runs.push(...parseInline(italic ?? italicAlt, { ...style, italic: true }));
    else {
      runs.push(...parseInline(linkText, style));
      if (linkUrl !== linkText) push(` (${linkUrl})`, style);
    }
    last = start + whole.length;
  }
  push(String(text || '').slice(last), style);
  return runs;
};

const plainText = (text) => parseInline(text).map((run) => run.text).join('');

/* ------------------------------------------------------------------ PDF */

/**
 * @param {Array<{role: string, text: string, at?: Date}>} messages  the chat, in order
 * @param {Object} opts
 * @param {string} opts.exportedBy   name of the person exporting
 * @param {Date|moment} opts.exportedAt
 * @param {Uint8Array} [opts.logo]  the FieldWorker wordmark PNG; text is used when absent
 * @returns {jsPDF}
 */
export const buildChatPdf = (messages, { exportedBy, exportedAt, logo } = {}) => {
  const doc = new jsPDF({ orientation: 'portrait', ...PAGE });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const bottom = pageHeight - MARGIN - FOOTER_HEIGHT;
  const exportedMoment = moment(exportedAt);

  let y = MARGIN;
  // Where content is drawn: the page between its margins, or the inside of the
  // reply bubble being written.
  let frame = { left: MARGIN, width: contentWidth };
  // The reply bubble being written: its border is drawn once its content is down,
  // one segment per page it spans.
  let bubble = null;

  const setFont = (size, style = 'normal', color = COLORS.text) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  const lineHeight = (size) => size * 1.35;

  const currentPage = () => doc.internal.getCurrentPageInfo().pageNumber;

  /* A reply bubble that runs onto the next page is closed here and reopened there.
     Nothing is drawn for it on this page when no content has gone in yet. */
  const newPage = () => {
    if (bubble && y > bubble.top + BUBBLE_PAD_Y + 2) {
      bubble.segments.push({ page: currentPage(), top: bubble.top, bottom: y + 4 });
    }
    doc.addPage();
    y = MARGIN;
    if (bubble) {
      bubble.top = y;
      y += BUBBLE_PAD_Y + 2;
    }
  };

  const ensureSpace = (height) => {
    if (y + height > bottom) newPage();
  };

  const setRunFont = (run, size, color) => {
    if (run.code) {
      doc.setFont('courier', run.bold ? 'bold' : 'normal');
    } else {
      const style = [run.bold && 'bold', run.italic && 'italic'].filter(Boolean).join('') || 'normal';
      doc.setFont('helvetica', style);
    }
    doc.setFontSize(size);
    doc.setTextColor(...(run.code ? COLORS.code : color));
  };

  /* Lays styled runs out in lines no wider than `width`, breaking at spaces.
     A word wider than a whole line (a long link) is cut to fit. */
  const layoutRuns = (runs, size, width) => {
    const lines = [];
    let line = [];
    let x = 0;
    let pendingSpace = false;
    const newLine = () => {
      lines.push(line);
      line = [];
      x = 0;
      pendingSpace = false;
    };

    runs.forEach((run) => {
      clean(run.text).split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (!part.trim()) {
          if (line.length) pendingSpace = true;
          return;
        }
        setRunFont(run, size, COLORS.text);
        const spaceWidth = pendingSpace ? doc.getTextWidth(' ') : 0;
        let word = part;
        let wordWidth = doc.getTextWidth(word);
        if (line.length && x + spaceWidth + wordWidth > width) newLine();
        else x += spaceWidth;

        while (wordWidth > width - x && word.length > 1) {
          let cut = word.length - 1;
          while (cut > 1 && doc.getTextWidth(word.slice(0, cut)) > width - x) cut -= 1;
          line.push({ run, text: word.slice(0, cut), x, width: doc.getTextWidth(word.slice(0, cut)) });
          newLine();
          word = word.slice(cut);
          wordWidth = doc.getTextWidth(word);
        }
        line.push({ run, text: word, x, width: wordWidth });
        x += wordWidth;
        pendingSpace = false;
      });
    });
    if (line.length) lines.push(line);
    return lines;
  };

  const sameStyle = (a, b) => !!a.bold === !!b.bold && !!a.italic === !!b.italic && !!a.code === !!b.code;

  /* Joins a laid-out line's words into one string per stretch of the same style.
     The PDF doesn't embed its fonts, so a viewer draws them with its own
     Helvetica, whose widths differ slightly from jsPDF's; placing every word at
     its own x lets that drift run a long word into the next one. */
  const lineSegments = (line) => line.reduce((segments, piece) => {
    const last = segments[segments.length - 1];
    if (last && sameStyle(last.run, piece.run)) {
      last.text += piece.x > last.end + 0.01 ? ` ${piece.text}` : piece.text;
      last.end = piece.x + piece.width;
    } else {
      segments.push({ run: piece.run, text: piece.text, x: piece.x, end: piece.x + piece.width });
    }
    return segments;
  }, []);

  /* Writes styled runs as a wrapped paragraph, breaking across pages line by line. */
  const writeRuns = (runs, { size = BODY_SIZE, color = COLORS.text, indent = 0, after = 4, bar = false } = {}) => {
    const lh = lineHeight(size);
    layoutRuns(runs, size, frame.width - indent).forEach((line) => {
      ensureSpace(lh);
      if (bar) {
        doc.setDrawColor(...COLORS.rule);
        doc.setLineWidth(2);
        doc.line(frame.left + indent - 8, y + 1, frame.left + indent - 8, y + lh - 1);
      }
      lineSegments(line).forEach(({ run, text, x }) => {
        setRunFont(run, size, color);
        doc.text(text, frame.left + indent + x, y + size);
      });
      y += lh;
    });
    y += after;
  };

  const writeText = (text, opts) => writeRuns([{ text }], opts);

  const writeHeading = (level, text) => {
    const size = HEADING_SIZES[level - 1];
    // Keep a heading with at least two lines of what follows it.
    ensureSpace(4 + lineHeight(size) + lineHeight(BODY_SIZE) * 2);
    y += 4;
    writeRuns(parseInline(text, { bold: true }), { size, after: 2 });
  };

  const writeList = (items) => {
    items.forEach((item) => {
      const indent = LIST_INDENT * (item.depth + 1);
      ensureSpace(lineHeight(BODY_SIZE));
      if (item.marker) {
        setFont(BODY_SIZE);
        doc.text(item.marker, frame.left + indent - 4, y + BODY_SIZE, { align: 'right' });
      } else {
        doc.setFillColor(...COLORS.text);
        doc.circle(frame.left + indent - 9, y + 6.5, 1.6, 'F');
      }
      writeRuns(parseInline(item.text), { indent, after: 2 });
    });
    y += 2;
  };

  const writeCode = (text) => {
    const size = 8.5;
    const pad = 6;
    const lh = lineHeight(size);
    doc.setFont('courier', 'normal');
    doc.setFontSize(size);
    const lines = clean(text.replace(/\t/g, '    ')).split('\n')
      .flatMap((line) => doc.splitTextToSize(line, frame.width - pad * 2));
    lines.forEach((line) => {
      ensureSpace(lh);
      doc.setFillColor(...COLORS.shade);
      doc.rect(frame.left, y, frame.width, lh, 'F');
      doc.setFont('courier', 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...COLORS.code);
      doc.text(line, frame.left + pad, y + size);
      y += lh;
    });
    y += 6;
  };

  const writeRule = () => {
    ensureSpace(10);
    y += 4;
    doc.setDrawColor(...COLORS.rule);
    doc.setLineWidth(0.6);
    doc.line(frame.left, y, frame.left + frame.width, y);
    y += 6;
  };

  const writeTable = (header, rows) => {
    const size = 8.5;
    const pad = 4;
    const lh = lineHeight(size);
    const columnCount = Math.max(header.length, ...rows.map((row) => row.length));
    const cells = [header, ...rows].map((row) => Array.from({ length: columnCount }, (_, c) => clean(plainText(row[c])).trim()));

    // Each column is as wide as its longest cell. When that overflows the page,
    // columns narrower than an even share keep their width and the wide ones
    // split what is left, so a short status column isn't squeezed by a long note.
    const natural = Array.from({ length: columnCount }, (_, c) => Math.max(24, ...cells.map((row, r) => {
      setFont(size, r === 0 ? 'bold' : 'normal');
      // +1: splitTextToSize wraps a cell exactly as wide as its column.
      return doc.getTextWidth(row[c]) + pad * 2 + 1;
    })));
    const naturalTotal = natural.reduce((sum, w) => sum + w, 0);
    let widths = natural;
    if (naturalTotal > frame.width) {
      const share = frame.width / columnCount;
      const narrowTotal = natural.filter((w) => w <= share).reduce((sum, w) => sum + w, 0);
      const wideTotal = naturalTotal - narrowTotal;
      widths = natural.map((w) => (w <= share ? w : (w / wideTotal) * (frame.width - narrowTotal)));
    }
    const tableWidth = widths.reduce((sum, w) => sum + w, 0);

    const layoutRow = (row, bold) => {
      setFont(size, bold ? 'bold' : 'normal');
      const wrapped = row.map((cell, c) => doc.splitTextToSize(cell, widths[c] - pad * 2));
      return { wrapped, bold, height: Math.max(1, ...wrapped.map((lines) => lines.length)) * lh + pad * 2 };
    };

    const drawRow = (row) => {
      if (row.bold) {
        doc.setFillColor(...COLORS.shade);
        doc.rect(frame.left, y, tableWidth, row.height, 'F');
      }
      doc.setDrawColor(...COLORS.rule);
      doc.setLineWidth(0.5);
      doc.rect(frame.left, y, tableWidth, row.height);
      let x = frame.left;
      row.wrapped.forEach((lines, c) => {
        if (c > 0) doc.line(x, y, x, y + row.height);
        setFont(size, row.bold ? 'bold' : 'normal');
        lines.forEach((line, l) => doc.text(line, x + pad, y + pad + size + l * lh));
        x += widths[c];
      });
      y += row.height;
    };

    const headerRow = layoutRow(cells[0], true);
    const bodyRows = cells.slice(1).map((row) => layoutRow(row, false));
    y += 2;
    ensureSpace(headerRow.height + (bodyRows[0] ? bodyRows[0].height : 0));
    drawRow(headerRow);
    bodyRows.forEach((row) => {
      // A table that runs onto a new page repeats its header there.
      if (y + row.height > bottom) {
        newPage();
        drawRow(headerRow);
      }
      drawRow(row);
    });
    y += 8;
  };

  const writeMarkdown = (markdown) => {
    parseMarkdown(markdown).forEach((block) => {
      if (block.type === 'heading') writeHeading(block.level, block.text);
      else if (block.type === 'list') writeList(block.items);
      else if (block.type === 'table') writeTable(block.header, block.rows);
      else if (block.type === 'code') writeCode(block.text);
      else if (block.type === 'quote') writeRuns(parseInline(block.text), { color: COLORS.muted, indent: 12, bar: true });
      else if (block.type === 'rule') writeRule();
      else writeRuns(parseInline(block.text));
    });
  };

  /* A prompt or error: plain text in a filled bubble sized to its longest line.
     Split into one bubble per page when it is too long for one. */
  const writePlainBubble = (text, { fill, color, align }) => {
    const size = BODY_SIZE;
    const lh = lineHeight(size);
    const lines = String(text || '').split('\n').flatMap((source) => (source.trim()
      ? layoutRuns([{ text: source }], size, contentWidth * PROMPT_MAX_WIDTH - BUBBLE_PAD_X * 2)
      : [[]]));
    const textWidth = Math.max(0, ...lines.map((line) => {
      const last = line[line.length - 1];
      return last ? last.x + last.width : 0;
    }));
    const width = textWidth + BUBBLE_PAD_X * 2;
    const left = align === 'right' ? MARGIN + contentWidth - width : MARGIN;

    let i = 0;
    while (i < lines.length) {
      ensureSpace(BUBBLE_PAD_Y * 2 + lh);
      const fit = Math.max(1, Math.floor((bottom - y - BUBBLE_PAD_Y * 2) / lh));
      const chunk = lines.slice(i, i + fit);
      const height = chunk.length * lh + BUBBLE_PAD_Y * 2;
      const top = y;
      doc.setFillColor(...fill);
      doc.roundedRect(left, top, width, height, BUBBLE_RADIUS, BUBBLE_RADIUS, 'F');
      chunk.forEach((line, l) => lineSegments(line).forEach(({ run, text: segment, x }) => {
        setRunFont(run, size, color);
        doc.text(segment, left + BUBBLE_PAD_X + x, top + BUBBLE_PAD_Y + size + l * lh);
      }));
      y = top + height;
      i += chunk.length;
      if (i < lines.length) newPage();
    }
  };

  /* Draws a reply bubble's border after its content: a white bubble needs no
     fill, so the border can go on last without covering anything. A bubble split
     across pages is left open at the break. */
  const drawReplyBorder = ({ left, width, segments }) => {
    const resumePage = currentPage();
    const right = left + width;
    segments.forEach((segment, i) => {
      doc.setPage(segment.page);
      doc.setDrawColor(...COLORS.replyBorder);
      doc.setLineWidth(0.8);
      const first = i === 0;
      const last = i === segments.length - 1;
      if (first && last) {
        doc.roundedRect(left, segment.top, width, segment.bottom - segment.top, BUBBLE_RADIUS, BUBBLE_RADIUS, 'S');
        return;
      }
      doc.line(left, segment.top, left, segment.bottom);
      doc.line(right, segment.top, right, segment.bottom);
      if (first) doc.line(left, segment.top, right, segment.top);
      if (last) doc.line(left, segment.bottom, right, segment.bottom);
    });
    doc.setPage(resumePage);
  };

  const writeReplyBubble = (markdown) => {
    const width = contentWidth * REPLY_WIDTH;
    bubble = { left: MARGIN, width, top: y, segments: [] };
    frame = { left: MARGIN + BUBBLE_PAD_X, width: width - BUBBLE_PAD_X * 2 };
    y += BUBBLE_PAD_Y + 2;
    writeMarkdown(markdown);
    // Every block ends with about 4pt of its own spacing.
    y += BUBBLE_PAD_Y - 4;
    bubble.segments.push({ page: currentPage(), top: bubble.top, bottom: y });
    drawReplyBorder(bubble);
    bubble = null;
    frame = { left: MARGIN, width: contentWidth };
  };

  /* ---------------------------------------------------------- title block */

  setFont(18, 'bold');
  doc.text(TITLE, MARGIN, y + 18);
  y += lineHeight(18);
  writeText(DISCLAIMER, { size: 8.5, color: COLORS.muted, after: 10 });
  writeText([`Exported: ${exportedMoment.format(TIMESTAMP_FORMAT)}`, exportedBy ? `by ${exportedBy}` : null].filter(Boolean).join(' '),
    { size: 9, color: COLORS.muted, after: 6 });
  writeRule();

  /* ------------------------------------------------------------- messages */

  messages.forEach((message, index) => {
    // A wider gap before each question marks where a new exchange starts.
    if (index > 0) y += message.role === 'user' ? 14 : 8;
    const isPrompt = message.role === 'user';

    const at = message.at ? moment(message.at) : null;
    const label = [
      ROLE_LABELS[message.role] || message.role,
      at ? at.format(at.isSame(exportedMoment, 'day') ? TIME_FORMAT : TIMESTAMP_FORMAT) : null,
    ].filter(Boolean).join('  -  ');
    // Keep the label with the start of its bubble: for a reply, room for an
    // opening heading and two lines, so the heading doesn't move to the next page alone.
    const opening = BUBBLE_PAD_Y * 2 + (message.role === 'assistant' ? 4 + lineHeight(HEADING_SIZES[0]) : 0);
    ensureSpace(lineHeight(8) + 3 + opening + lineHeight(BODY_SIZE) * 2);
    setFont(8, 'bold', ROLE_COLORS[message.role] || COLORS.muted);
    if (isPrompt) doc.text(clean(label), MARGIN + contentWidth, y + 8, { align: 'right' });
    else doc.text(clean(label), MARGIN + 2, y + 8);
    y += lineHeight(8) + 3;

    if (message.role === 'assistant') {
      writeReplyBubble(message.text);
    } else {
      // Typed questions and errors are plain text; keep the user's line breaks.
      writePlainBubble(message.text, isPrompt
        ? { fill: COLORS.promptBubble, color: COLORS.promptText, align: 'right' }
        : { fill: COLORS.errorBubble, color: COLORS.errorText, align: 'left' });
    }
  });

  /* ---------------------------------------------------------------- footer */

  /* "Generated using" and the FieldWorker wordmark, from the left margin, on the
     footer's baseline. The wordmark falls back to the name in text. */
  const writeCredit = (footerY) => {
    setFont(7.5, 'normal', COLORS.muted);
    const label = 'Generated using';
    doc.text(label, MARGIN, footerY);
    const logoLeft = MARGIN + doc.getTextWidth(label) + 4;
    if (logo) {
      const logoHeight = LOGO.drawWidth * (LOGO.height / LOGO.width);
      // Centred on the text's x-height; FAST, or jsPDF stores the image
      // uncompressed and a 10 KB logo takes the file past 100 KB.
      doc.addImage(logo, 'PNG', logoLeft, footerY - 2.7 - logoHeight / 2, LOGO.drawWidth, logoHeight, 'fieldworker-logo', 'FAST');
    } else {
      setFont(7.5, 'bold', BRAND);
      doc.text('FieldWorker', logoLeft, footerY);
    }
  };

  const pageCount = doc.getNumberOfPages();
  const footerCenter = clean(`${TITLE}  |  Exported ${exportedMoment.format(TIMESTAMP_FORMAT)}`);
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    const footerY = pageHeight - MARGIN;
    doc.setDrawColor(...COLORS.rule);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, footerY - 14, pageWidth - MARGIN, footerY - 14);
    writeCredit(footerY);
    setFont(7.5, 'normal', COLORS.muted);
    doc.text(footerCenter, pageWidth / 2, footerY, { align: 'center' });
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - MARGIN, footerY, { align: 'right' });
  }

  return doc;
};

let logoBytes = null;

/** Loads the wordmark once, ahead of any export (AiCompanion calls this on mount),
    so the export itself stays synchronous inside the click. A save that first
    waits on a fetch can lose the click's user activation, and Chrome then treats
    it as an automatic download and may block it. Until it loads, or if it never
    does, the header credits FieldWorker in text. */
export const preloadLogo = () => {
  if (logoBytes) return;
  fetch(fieldworkerLogo)
    .then((response) => (response.ok ? response.arrayBuffer() : null))
    .then((buffer) => {
      if (buffer) logoBytes = new Uint8Array(buffer);
    })
    .catch(() => {});
};

/** Builds the PDF and hands it to the browser as a download. */
export const downloadChatPdf = (messages, opts = {}) => {
  const doc = buildChatPdf(messages, { ...opts, logo: logoBytes });
  doc.save(`AI_Companion_Conversation_${moment(opts.exportedAt).format('YYYY-MM-DD_HHmm')}.pdf`);
};
